import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CSharpUtils from '../../felles/CSharpUtils';
import { bulkImportNR01StockGruppe, fetchNR01StockGruppeOverview } from '../../felles/APICalls_NR01';
import { NR01BulkImportStockGruppeItem, NR01BulkImportResultItem, NR01StockGruppeItem } from '../../models/NR01StockGruppeOverview';
import { AppState } from '../../store/rootReducer';
import * as actionsNR01 from '../../store/actions/indexNR01';

interface NR01StockGruppeOversiktProps {
  size: { width: number; height: number };
}

interface CreateFormState {
  groupNumber: string;
  groupName: string;
  statisticsGroup: string;
  account: string;
  costCenter: string;
  profitPercentage: string;
}

interface EditFormState {
  groupNumber: string;
  groupName: string;
  statisticsGroup: string;
  account: string;
  costCenter: string;
  profitPercentage: string;
}

const emptyCreateForm: CreateFormState = {
  groupNumber: '',
  groupName: '',
  statisticsGroup: '',
  account: '',
  costCenter: '',
  profitPercentage: '',
};

const emptyEditForm: EditFormState = {
  groupNumber: '',
  groupName: '',
  statisticsGroup: '',
  account: '',
  costCenter: '',
  profitPercentage: '',
};

export default function NR01StockGruppeOversikt({ size }: NR01StockGruppeOversiktProps) {
  const dispatch = useDispatch();
  const companyId = Number(CSharpUtils.T_companyID) || 0;

  const data = useSelector((state: AppState) => state.NR01?.data);
  const isFetching = useSelector((state: AppState) => state.NR01?.isFetching);
  const isDeleting = useSelector((state: AppState) => state.NR01?.isDeleting);
  const isCreating = useSelector((state: AppState) => state.NR01?.isCreating);
  const isUpdating = useSelector((state: AppState) => state.NR01?.isUpdating);
  const reduxError = useSelector((state: AppState) => state.NR01?.error);
  const createResult = useSelector((state: AppState) => state.NR01?.createResult);
  const updateResult = useSelector((state: AppState) => state.NR01?.updateResult);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);
  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);
  const [createError, setCreateError] = useState('');
  const [editError, setEditError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteGroupNumber, setDeleteGroupNumber] = useState('');
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState('');

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResultDialogOpen, setImportResultDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState<NR01BulkImportResultItem[]>([]);
  const [importSummary, setImportSummary] = useState({ total: 0, success: 0, failed: 0, message: '' });
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editSubmittedRef = useRef<boolean>(false);
  const createSubmittedRef = useRef<boolean>(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  // Route Redux errors to whichever dialog is open, or to a toast if none is.
  useEffect(() => {
    if (!reduxError) return;
    if (createDialogOpen) {
      setCreateError(reduxError);
    } else if (editDialogOpen) {
      setEditError(reduxError);
    } else {
      setErrorSnackbarMessage(reduxError);
    }
  }, [reduxError]);

  // Close create dialog on successful creation
  useEffect(() => {
    if (createResult && createDialogOpen && !isCreating && createSubmittedRef.current) {
      createSubmittedRef.current = false;
      setCreateDialogOpen(false);
      setCreateForm(emptyCreateForm);
      setCreateError('');
    }
  }, [createResult, createDialogOpen, isCreating]);

  // Close edit dialog on successful update
  useEffect(() => {
    if (updateResult && editDialogOpen && !isUpdating && editSubmittedRef.current) {
      editSubmittedRef.current = false;
      setEditDialogOpen(false);
      setEditForm(emptyEditForm);
      setEditError('');
    }
  }, [updateResult, editDialogOpen, isUpdating]);

  useEffect(() => {
    if (companyId > 0) {
      dispatch(actionsNR01.fetchNR01StockGruppeRequest(
        companyId,
        page + 1,
        rowsPerPage,
        debouncedSearch || undefined,
        sortBy,
        sortDirection,
      ));
    }
  }, [dispatch, companyId, page, rowsPerPage, debouncedSearch, sortBy, sortDirection]);

  const items = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleDelete = (groupNumber: string) => {
    if (!groupNumber) return;
    setDeleteGroupNumber(groupNumber);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteGroupNumber) {
      dispatch(actionsNR01.deleteNR01StockGruppeRequest(companyId, deleteGroupNumber));
    }
    setDeleteDialogOpen(false);
    setDeleteGroupNumber('');
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteGroupNumber('');
  };

  const handleEdit = (row: any) => {
    setEditForm({
      groupNumber: row.groupNumber,
      groupName: row.groupName,
      statisticsGroup: row.statisticsGroup || '',
      account: row.account || '',
      costCenter: row.costCenter || '',
      profitPercentage: row.profitPercentage ? String(row.profitPercentage) : '',
    });
    editSubmittedRef.current = false;
    setEditDialogOpen(true);
    setEditError('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleExportToExcel = async () => {
    try {
      // Fetch all data matching the current search/sort, without pagination
      const response = await fetchNR01StockGruppeOverview(
        companyId,
        1,
        10000,
        debouncedSearch || undefined,
        sortBy,
        sortDirection,
      );

      const exportItems: NR01StockGruppeItem[] = response.data.content || [];

      if (exportItems.length === 0) {
        alert('Ingen data å eksportere.');
        return;
      }

      // Create CSV content with semicolon separator for Excel compatibility
      const headers = ['VGR', 'Navn Varegruppe', 'STAT', 'BRTFJ', 'KONTO', 'STED'];
      const rows = exportItems.map((item) => [
        item.groupNumber || '',
        item.groupName || '',
        item.statisticsGroup || '',
        item.profitPercentage != null ? String(item.profitPercentage) : '',
        item.account || '',
        item.costCenter || '',
      ]);

      // Helper function to escape CSV values
      const escapeCSV = (value: string): string => {
        // If value contains semicolon, newline, or quotes, wrap in quotes and escape existing quotes
        if (value.includes(';') || value.includes('\n') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      // Convert to CSV with semicolon separator (Excel compatible in European locales)
      const csvContent = [
        headers.join(';'),
        ...rows.map((row: string[]) => row.map(escapeCSV).join(';')),
      ].join('\r\n');

      // Add BOM for UTF-8 encoding recognition in Excel
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Varegruppeoversikt_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Eksport feil:', error);
      alert('Feil under eksport. Prøv igjen.');
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCreateDialog = () => {
    createSubmittedRef.current = false;
    setCreateDialogOpen(true);
    setCreateForm(emptyCreateForm);
    setCreateError('');
  };

  const closeCreateDialog = () => {
    if (isCreating) return;
    setCreateDialogOpen(false);
  };

  const openEditDialog = () => {
    setEditDialogOpen(true);
    setEditError('');
  };

  const closeEditDialog = () => {
    if (isUpdating) return;
    setEditDialogOpen(false);
  };

  const onCreateFieldChange = (field: keyof CreateFormState, value: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const onEditFieldChange = (field: keyof EditFormState, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitCreate = () => {
    const groupNumber = createForm.groupNumber.trim();
    const groupName = createForm.groupName.trim();

    if (groupNumber.length !== 5) {
      setCreateError('VGR må være nøyaktig 5 tegn.');
      return;
    }

    if (!groupName) {
      setCreateError('Navn varegruppe er obligatorisk.');
      return;
    }

    setCreateError('');
    createSubmittedRef.current = true;

    dispatch(actionsNR01.createNR01StockGruppeRequest(companyId, {
      groupNumber,
      groupName,
      statisticsGroup: createForm.statisticsGroup.trim(),
      account: createForm.account.trim(),
      costCenter: createForm.costCenter.trim(),
      profitPercentage: createForm.profitPercentage.trim()
        ? Number(createForm.profitPercentage)
        : undefined,
    }));

    // Dialog stays open - will close on success via useEffect
  };

  const submitEdit = () => {
    const groupNumber = editForm.groupNumber.trim();
    const groupName = editForm.groupName.trim();

    if (!groupNumber) {
      setEditError('VGR er obligatorisk.');
      return;
    }

    if (!groupName) {
      setEditError('Navn varegruppe er obligatorisk.');
      return;
    }

    setEditError('');
    editSubmittedRef.current = true;

    dispatch(actionsNR01.updateNR01StockGruppeRequest(companyId, groupNumber, {
      groupNumber,
      groupName,
      statisticsGroup: editForm.statisticsGroup.trim(),
      account: editForm.account.trim(),
      costCenter: editForm.costCenter.trim(),
      profitPercentage: editForm.profitPercentage.trim()
        ? Number(editForm.profitPercentage)
        : undefined,
    }));

    // Dialog stays open - will close on success via useEffect
  };

  // Header names (case-insensitive) accepted for each field. The header row decides
  // column order, so the file does not need to match the export's column order,
  // and optional columns may be omitted entirely.
  const COLUMN_ALIASES: Record<keyof NR01BulkImportStockGruppeItem, string[]> = {
    groupNumber: ['VGR'],
    groupName: ['NAVN VAREGRUPPE', 'NAVN'],
    statisticsGroup: ['STAT GR', 'STAT'],
    profitPercentage: ['BRTFJ'],
    account: ['KONTO'],
    costCenter: ['STED'],
  };

  const splitCsvLine = (line: string): string[] =>
    line.split(';').map(cell => cell.trim().replace(/^"|"$/g, ''));

  // Parse CSV/Excel file and extract data.
  // Column order is read from the header row - only VGR and Navn Varegruppe are required.
  const parseCSVFile = (content: string): NR01BulkImportStockGruppeItem[] => {
    const lines = content.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length < 2) return []; // Need at least header + 1 row

    const headerCells = splitCsvLine(lines[0]).map(h => h.toUpperCase());
    const columnIndex = {} as Record<keyof NR01BulkImportStockGruppeItem, number>;
    (Object.keys(COLUMN_ALIASES) as Array<keyof NR01BulkImportStockGruppeItem>).forEach(field => {
      const aliases = COLUMN_ALIASES[field];
      columnIndex[field] = headerCells.findIndex(h => aliases.includes(h));
    });

    const getCell = (cells: string[], field: keyof NR01BulkImportStockGruppeItem): string | undefined => {
      const idx = columnIndex[field];
      return idx >= 0 ? cells[idx] : undefined;
    };

    const items: NR01BulkImportStockGruppeItem[] = [];

    // Skip header row (first line)
    for (let i = 1; i < lines.length; i++) {
      const cells = splitCsvLine(lines[i]);
      const groupNumber = getCell(cells, 'groupNumber');

      if (groupNumber) {
        const profitPercentageRaw = getCell(cells, 'profitPercentage');
        const profitPercentage = profitPercentageRaw ? Number(profitPercentageRaw.replace(',', '.')) : undefined;
        items.push({
          groupNumber,
          groupName: getCell(cells, 'groupName') || '',
          statisticsGroup: getCell(cells, 'statisticsGroup') || undefined,
          profitPercentage: profitPercentage || undefined,
          account: getCell(cells, 'account') || undefined,
          costCenter: getCell(cells, 'costCenter') || undefined,
        });
      }
    }
    return items;
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
    setImportError('');
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input for re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImportError('');
    setIsImporting(true);

    try {
      const text = await file.text();
      const items = parseCSVFile(text);

      if (items.length === 0) {
        setImportError('Fant ingen gyldige rader i filen. Sørg for at filen har samme format som eksportert CSV.');
        setIsImporting(false);
        return;
      }

      // Call bulk import API
      const response = await bulkImportNR01StockGruppe(companyId, items);

      setImportResults(response.data.results);
      setImportSummary({
        total: response.data.totalCount,
        success: response.data.successCount,
        failed: response.data.failedCount,
        message: response.data.message,
      });

      setImportDialogOpen(false);
      setImportResultDialogOpen(true);

      // Refresh the list if any imports succeeded
      if (response.data.successCount > 0) {
        dispatch(actionsNR01.fetchNR01StockGruppeRequest(
          companyId,
          page + 1,
          rowsPerPage,
          debouncedSearch || undefined,
          sortBy,
          sortDirection,
        ));
      }
    } catch (error: any) {
      console.error('Import error:', error);
      setImportError(error.response?.data?.message || 'Import feilet. Sjekk fil-formatet og prøv igjen.');
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    if (isImporting) return;
    setImportDialogOpen(false);
  };

  const closeImportResultDialog = () => {
    setImportResultDialogOpen(false);
    setImportResults([]);
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Varegruppeoversikt
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabellen viser alle registrerte varegrupper.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip
            arrow
            slotProps={{ tooltip: { sx: { maxWidth: 480 } } }}
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Denne oversikten viser alle registrerte varegrupper.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Når du søker i tabellen, søker programmet i hele innholdet i alle kolonner – ikke bare fra
                  starten av teksten. Ønsker du for eksempel å finne alle varegrupper som starter med 3, kan
                  du skrive 3*. Da vises kun varegrupper der første siffer er 3.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                  Forklaring til feltene:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" variant="body2">Varegruppe: Numerisk, 5 siffer</Typography>
                  <Typography component="li" variant="body2">Navn varegruppe: Egendefinert tekst på varegruppen</Typography>
                  <Typography component="li" variant="body2">STAT GR – Statistikkgruppe fra TAB1, Tabell 04</Typography>
                  <Typography component="li" variant="body2">BRTFJ – For fast bruttofortjeneste (brukes til skaffevarer)</Typography>
                  <Typography component="li" variant="body2">KONTO – For overføring av salg til fast konto</Typography>
                  <Typography component="li" variant="body2">STED – Kostnadssted</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                  Funksjoner:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" variant="body2">+ Ny Rad – Her kan du legge inn en enkelt varegruppe</Typography>
                  <Typography component="li" variant="body2">
                    Eksporter til Excel – Fil med hele ditt varegruppearkiv. Har du gjort utplukk i søkefelter
                    er det dette resultatet som kommer i filen.
                  </Typography>
                  <Typography component="li" variant="body2">Importer fra Excel – Her kan du importere nye varegrupper.</Typography>
                </Box>
              </Box>
            }
          >
            <IconButton size="small" sx={{ color: '#28a745' }}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Søk i tabellen... (f.eks. 8* for alle som starter på 8)"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ width: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Ny rad
          </Button>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportToExcel}>
            Eksporter til Excel
          </Button>
          <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={handleImportClick}>
            Importer fra Excel
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Viser {items.length} av {totalCount} rader
      </Typography>

      <TableContainer sx={{ maxHeight: size.height ? size.height - 330 : 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'VGR'}
                  direction={sortBy === 'VGR' ? sortDirection : 'asc'}
                  onClick={() => handleSort('VGR')}
                >
                  VGR
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'NAVN'}
                  direction={sortBy === 'NAVN' ? sortDirection : 'asc'}
                  onClick={() => handleSort('NAVN')}
                >
                  Navn Varegruppe
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'STAT'}
                  direction={sortBy === 'STAT' ? sortDirection : 'asc'}
                  onClick={() => handleSort('STAT')}
                >
                  STAT
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'BRTFJ'}
                  direction={sortBy === 'BRTFJ' ? sortDirection : 'asc'}
                  onClick={() => handleSort('BRTFJ')}
                >
                  BRTFJ
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'KONTO'}
                  direction={sortBy === 'KONTO' ? sortDirection : 'asc'}
                  onClick={() => handleSort('KONTO')}
                >
                  KONTO
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'STED'}
                  direction={sortBy === 'STED' ? sortDirection : 'asc'}
                  onClick={() => handleSort('STED')}
                >
                  STED
                </TableSortLabel>
              </TableCell>
              <TableCell>Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Laster data...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Ingen data funnet</TableCell>
              </TableRow>
            ) : (
              items.map((row, index) => (
                <TableRow key={`${row.groupNumber}-${index}`} hover>
                  <TableCell>{row.groupNumber}</TableCell>
                  <TableCell>{row.groupName}</TableCell>
                  <TableCell>{row.statisticsGroup}</TableCell>
                  <TableCell>{row.profitPercentage ?? ''}</TableCell>
                  <TableCell>{row.account}</TableCell>
                  <TableCell>{row.costCenter}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        disabled={isUpdating}
                        onClick={() => handleEdit(row)}
                        sx={{
                          color: '#333',
                          border: '1px solid #ddd',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#28a745',
                            color: '#fff',
                            borderColor: '#28a745',
                          },
                          '&:disabled': {
                            color: '#999',
                            borderColor: '#ddd',
                          },
                        }}
                      >
                        Rediger
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={isDeleting}
                        onClick={() => handleDelete(row.groupNumber)}
                        sx={{
                          color: '#333',
                          border: '1px solid #ddd',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            borderColor: '#dc3545',
                          },
                          '&:disabled': {
                            color: '#999',
                            borderColor: '#ddd',
                          },
                        }}
                      >
                        Slett
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100, 250]}
        labelRowsPerPage="Rader per side:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} av ${count}`}
      />

      <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Legg til ny rad</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, mt: 1 }}
              onClose={() => setCreateError('')}
            >
              {createError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            <TextField
              label="VGR"
              value={createForm.groupNumber}
              onChange={(e) => onCreateFieldChange('groupNumber', e.target.value)}
              inputProps={{ maxLength: 5 }}
              error={createError.toLowerCase().includes('finnes') || createError.toLowerCase().includes('vgr')}
              helperText={createError.toLowerCase().includes('finnes') ? 'Gruppenummeret er allerede i bruk' : ''}
            />
            <TextField
              label="Navn Varegruppe"
              value={createForm.groupName}
              onChange={(e) => onCreateFieldChange('groupName', e.target.value)}
              inputProps={{ maxLength: 36 }}
            />
            <TextField
              label="STAT"
              value={createForm.statisticsGroup}
              onChange={(e) => onCreateFieldChange('statisticsGroup', e.target.value)}
              inputProps={{ maxLength: 3 }}
            />
            <TextField
              label="KONTO"
              value={createForm.account}
              onChange={(e) => onCreateFieldChange('account', e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="STED"
              value={createForm.costCenter}
              onChange={(e) => onCreateFieldChange('costCenter', e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="BRTFJ"
              value={createForm.profitPercentage}
              onChange={(e) => onCreateFieldChange('profitPercentage', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateDialog} disabled={isCreating}>Avbryt</Button>
          <Button variant="contained" onClick={submitCreate} disabled={isCreating}>
            {isCreating ? 'Lagrer...' : 'Legg til'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Rediger varegruppe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            VGR og Navn Varegruppe er obligatoriske. VGR må være nøyaktig 5 tegn, STAT GR må være nøyaktig
            tre siffer, og KONTO og STED må være nøyaktig fire siffer. De øvrige feltene er valgfrie og kan stå tomme.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            <TextField
              label="VGR"
              value={editForm.groupNumber}
              onChange={(e) => onEditFieldChange('groupNumber', e.target.value)}
              inputProps={{ maxLength: 5 }}
              disabled
            />
            <TextField
              label="Navn Varegruppe"
              value={editForm.groupName}
              onChange={(e) => onEditFieldChange('groupName', e.target.value)}
              inputProps={{ maxLength: 36 }}
            />
            <TextField
              label="STAT"
              value={editForm.statisticsGroup}
              onChange={(e) => onEditFieldChange('statisticsGroup', e.target.value)}
              inputProps={{ maxLength: 3 }}
            />
            <TextField
              label="KONTO"
              value={editForm.account}
              onChange={(e) => onEditFieldChange('account', e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="STED"
              value={editForm.costCenter}
              onChange={(e) => onEditFieldChange('costCenter', e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="BRTFJ"
              value={editForm.profitPercentage}
              onChange={(e) => onEditFieldChange('profitPercentage', e.target.value)}
            />
          </Box>
          {editError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {editError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEditDialog} disabled={isUpdating}>Avbryt</Button>
          <Button variant="contained" onClick={submitEdit} disabled={isUpdating}>Oppdater</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteOutlineIcon sx={{ color: '#dc3545', fontSize: 28 }} />
            <Typography variant="h6" component="span">
              Bekreft sletting
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Er du sikker på at du vil slette varegruppe <strong>{deleteGroupNumber}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Denne handlingen kan ikke angres.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={cancelDelete} 
            disabled={isDeleting}
            variant="outlined"
          >
            Avbryt
          </Button>
          <Button 
            onClick={confirmDelete} 
            disabled={isDeleting}
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineIcon />}
          >
            {isDeleting ? 'Sletter...' : 'Slett'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileUploadIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" component="span">
              Importer fra Excel
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Velg en CSV-fil med varegrupper. Filen bør ha kolonnene:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
            VGR;Navn Varegruppe;STAT;BRTFJ;KONTO;STED
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Kolonnene gjenkjennes på navnet i første rad, så rekkefølgen er valgfri.
            VGR må være nøyaktig 5 tegn. STAT/BRTFJ/KONTO/STED er valgfrie.
          </Typography>

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}

          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="import-file-input"
          />
          <label htmlFor="import-file-input">
            <Button
              variant="contained"
              component="span"
              disabled={isImporting}
              startIcon={<FileUploadIcon />}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {isImporting ? 'Importerer...' : 'Velg fil'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImportDialog} disabled={isImporting} variant="outlined">
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={importResultDialogOpen} onClose={closeImportResultDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {importSummary.failed === 0 ? (
              <CheckCircleIcon sx={{ color: '#28a745', fontSize: 28 }} />
            ) : importSummary.success > 0 ? (
              <InfoOutlinedIcon sx={{ color: '#ffc107', fontSize: 28 }} />
            ) : (
              <ErrorIcon sx={{ color: '#dc3545', fontSize: 28 }} />
            )}
            <Typography variant="h6" component="span">
              Import-resultat
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity={importSummary.failed === 0 ? 'success' : importSummary.success > 0 ? 'warning' : 'error'} 
            sx={{ mb: 2 }}
          >
            {importSummary.message}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Totalt:</strong> {importSummary.total}
            </Typography>
            <Typography variant="body2" sx={{ color: '#28a745' }}>
              <strong>Opprettet:</strong> {importSummary.success}
            </Typography>
            <Typography variant="body2" sx={{ color: '#dc3545' }}>
              <strong>Feilet:</strong> {importSummary.failed}
            </Typography>
          </Box>

          {importResults.length > 0 && (
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>Status</TableCell>
                    <TableCell sx={{ width: 100 }}>VGR</TableCell>
                    <TableCell>Navn</TableCell>
                    <TableCell>Melding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResults.map((result, index) => (
                    <TableRow key={index} sx={{ backgroundColor: result.success ? 'inherit' : '#fff5f5' }}>
                      <TableCell>
                        {result.success ? (
                          <CheckCircleIcon sx={{ color: '#28a745', fontSize: 20 }} />
                        ) : (
                          <ErrorIcon sx={{ color: '#dc3545', fontSize: 20 }} />
                        )}
                      </TableCell>
                      <TableCell>{result.groupNumber}</TableCell>
                      <TableCell>{result.groupName}</TableCell>
                      <TableCell>{result.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImportResultDialog} variant="contained">
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorSnackbarMessage}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbarMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnackbarMessage('')}>
          {errorSnackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
